/**
 * Welcome Modal Component
 *
 * Shown on first visit to each page. Offers the user a choice:
 * - "Take the Tour" -- starts the Intro.js walkthrough
 * - "Skip for Now" -- marks the page as skipped
 * - "Don't show walkthroughs" checkbox -- global opt-out
 */

import { useState } from 'react';
import { HelpCircle, X } from 'lucide-react';

interface WelcomeModalProps {
  pageName: string;
  onStartTour: () => void;
  onSkip: () => void;
  onGlobalOptOut: () => void;
  open: boolean;
}

export function WelcomeModal({
  pageName,
  onStartTour,
  onSkip,
  onGlobalOptOut,
  open,
}: WelcomeModalProps) {
  const [dontShow, setDontShow] = useState(false);

  if (!open) return null;

  const handleSkip = () => {
    if (dontShow) {
      onGlobalOptOut();
    } else {
      onSkip();
    }
  };

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50">
      <div className="relative mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute right-3 top-3 rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {/* Icon */}
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-50">
          <HelpCircle className="h-7 w-7 text-blue-600" />
        </div>

        {/* Title */}
        <h2 className="mb-2 text-center text-lg font-semibold text-gray-900">
          Welcome to {pageName}
        </h2>

        {/* Description */}
        <p className="mb-6 text-center text-sm text-gray-600">
          Would you like a guided walkthrough of this page? We'll highlight each
          feature and explain how it works.
        </p>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <button
            onClick={onStartTour}
            className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Take the Tour
          </button>
          <button
            onClick={handleSkip}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Skip for Now
          </button>
        </div>

        {/* Don't show again checkbox */}
        <label className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
          <input
            type="checkbox"
            checked={dontShow}
            onChange={(e) => setDontShow(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Don't show walkthroughs for me
        </label>
      </div>
    </div>
  );
}
